import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, Box, Grid, Icon } from '@chakra-ui/react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  MdOutlineHandshake,
  MdOutlinePeopleAlt,
  MdNotes,
  MdOutlineLogout,
  MdOutlineShoppingCart,
  MdOutlineShopTwo,
  MdOutlineSettings,
  MdOutlineBusinessCenter,
  MdOutlineSummarize,
  MdHome,
} from 'react-icons/md';
import {
  CAMPAIGN_LIST,
  PROPOSAL_LIST,
  PRODUCT_LIST,
  MARKET_LIST,
  CLIENT_LIST,
  USER_LIST,
  AD_FORMAT_LIST,
  TRADING_LIST,
  HOME_DASHBOARD,
} from 'routes';
import {
  CAN_VIEW_CAMPAIGNS,
  CAN_VIEW_PROPOSALS,
  CAN_EDIT_CAMPAIGNS,
  CAN_EDIT_CLIENTS,
  CAN_EDIT_PRODUCTS,
  CAN_EDIT_USERS,
  CAN_EDIT_PRODUCT_METADATA,
  CAN_GENERATE_TRADING_REPORT,
} from 'consts/permissions';
import { logout } from 'store/user';
import userHasRequiredPermissions from 'hooks/useUserPermissions';
import Logo from './Logo';
import { useTranslation } from 'react-i18next';

const MenuOption = ({ label, icon, ...props }) => {
  const location = useLocation();
  const isActive = location.pathname.includes(props.to);

  return (
    <Grid
      templateAreas={`
    "icon"
    "label"
  `}
      gap={2}
      alignItems='center'
      {...props}
    >
      <Box
        width='fit-content'
        gridArea='icon'
        mx={5}
        px={2}
        bg={isActive ? 'white.100' : 'WPPGreen.300'}
        borderRadius='md'
        _hover={{
          bg: 'white.100',
        }}
      >
        <Icon
          as={icon}
          color='WPPNavy.300'
          display='block'
          boxSize={5}
          mx={3}
          my={2}
        />
      </Box>
      <Box gridArea='label' fontSize='xs' textAlign='center' px={1}>
        {label}
      </Box>
    </Grid>
  );
};
MenuOption.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.any,
  to: PropTypes.string,
};

const Link = ({ label, icon, to, permissions, ...props }) => {
  const hasPermissions = userHasRequiredPermissions(permissions);
  return hasPermissions ? (
    <MenuOption label={label} icon={icon} as={RouterLink} to={to} {...props} />
  ) : null;
};

Link.propTypes = {
  label: PropTypes.string.isRequired,
  to: PropTypes.string,
  icon: PropTypes.any,
  permissions: PropTypes.arrayOf(PropTypes.string),
};

const User = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoggedIn } = useSelector(state => state.user);

  const handleClick = () => dispatch(logout());

  if (!isLoggedIn) return null;

  return (
    <>
      <MenuOption
        label={t('logout')}
        icon={MdOutlineLogout}
        onClick={handleClick}
      />
    </>
  );
};

const Sidebar = ({ gridArea }) => {
  const { t } = useTranslation();

  return (
    <Grid
      gridArea={gridArea}
      py={4}
      gap={6}
      w='min-content'
      height='100%'
      bg='WPPNavy.300'
      color='white.100'
      templateRows='1fr min-content'
      overflow='hidden'
    >
      <Stack direction='column' spacing={4} marginTop={-4}>
        <RouterLink to={CAMPAIGN_LIST}>
          <Logo mt={8} mb={4} px={4} />
        </RouterLink>
        <Link
          to={HOME_DASHBOARD}
          permissions={[CAN_VIEW_CAMPAIGNS]}
          label={t('home_one', 'home')}
          icon={MdHome}
        />
        <Link
          to={MARKET_LIST}
          permissions={[CAN_EDIT_CAMPAIGNS]}
          label={t('market_one', 'Market')}
          icon={MdOutlineShoppingCart}
        />
        <Link
          to={PROPOSAL_LIST}
          permissions={[CAN_VIEW_PROPOSALS]}
          label={t('proposal_other', 'Transakcje')}
          icon={MdOutlineHandshake}
        />
        <Link
          to={CAMPAIGN_LIST}
          permissions={[CAN_VIEW_CAMPAIGNS]}
          label={t('campaign_other', 'Kampanie')}
          icon={MdNotes}
        />
        <Link
          to={PRODUCT_LIST}
          permissions={[CAN_EDIT_PRODUCTS]}
          label={t('product_other', 'Produkty')}
          icon={MdOutlineShopTwo}
        />
        <Link
          to={CLIENT_LIST}
          permissions={[CAN_EDIT_CLIENTS]}
          label={t('client_other', 'Klienci')}
          icon={MdOutlineBusinessCenter}
        />
        <Link
          to={USER_LIST}
          permissions={[CAN_EDIT_USERS]}
          label={t('user_other', 'Użytkownicy')}
          icon={MdOutlinePeopleAlt}
        />
        <Link
          to={AD_FORMAT_LIST}
          permissions={[CAN_EDIT_PRODUCT_METADATA]}
          label={t('product_settings', 'Ustawienia produktu')}
          icon={MdOutlineSettings}
        />
        <Link
          to={TRADING_LIST}
          permissions={[CAN_GENERATE_TRADING_REPORT]}
          label={t('trading_one', 'Trading')}
          icon={MdOutlineSummarize}
        />
      </Stack>
      <User />
    </Grid>
  );
};

Sidebar.propTypes = {
  gridArea: PropTypes.string,
};

export default Sidebar;
